import { createClubSchema, type ClubDetailDto, type UpdateClubInput } from '@rw/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Sheet } from '../../components/Sheet';
import { Button } from '../../components/ui';
import { invalidateClubs } from '../../hooks/queries';
import { ApiError } from '../../lib/api';
import { squareLogo } from '../../lib/resize-image';
import { useNetwork } from '../../stores/network';
import { toast } from '../../stores/toasts';
import { useT } from '../../i18n';
import { ClubBadge } from './ClubBadge';
import { clubApi } from './useClubActions';

const field = 'mt-1 w-full rounded-xl border border-ink-700 bg-ink-900 outline-none focus:border-volt';
const labelCls = 'mt-3 block text-xs font-semibold uppercase tracking-widest text-ink-400';

/** Create a club, or (with `club`) let its owner edit the name, description and logo. */
export function ClubFormSheet({ open, onClose, club, onSaved }: { open: boolean; onClose: () => void; club?: ClubDetailDto; onSaved?: (id: string) => void }) {
  const t = useT();
  return (
    <Sheet open={open} onClose={onClose} label={t(club ? 'Edit club' : 'Create club')}>
      {/* Mounted only while open, so every opening starts from the club's current values. */}
      <ClubForm club={club} onDone={(id) => (onClose(), onSaved?.(id))} />
    </Sheet>
  );
}

function ClubForm({ club, onDone }: { club?: ClubDetailDto; onDone: (id: string) => void }) {
  const [name, setName] = useState(club?.name ?? '');
  const [description, setDescription] = useState(club?.description ?? '');
  // undefined = unchanged, null = remove, string = new logo (data URL).
  const [logo, setLogo] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const online = useNetwork((s) => s.online);
  const qc = useQueryClient();
  const t = useT();

  const shownLogo = logo === undefined ? (club?.logoUrl ?? null) : logo;
  const preview = { id: club?.id ?? 'new', name: name || '?', color: club?.color ?? '#c6ff3d', logoUrl: shownLogo };

  const pick = async (file: File) => {
    try {
      setLogo(await squareLogo(file));
    } catch {
      toast({ kind: 'error', title: t('Could not read that image') });
    }
  };

  const submit = async () => {
    const parsed = createClubSchema.safeParse({ name, description });
    if (!parsed.success) return setError(t(parsed.error.issues[0]?.message ?? 'Invalid'));
    setError(null);
    setBusy(true);
    try {
      let id = club?.id;
      if (!id) {
        id = (await clubApi.create(parsed.data)).id;
      } else {
        const changes: UpdateClubInput = {};
        if (parsed.data.name !== club!.name) changes.name = parsed.data.name;
        if (parsed.data.description !== club!.description) changes.description = parsed.data.description;
        if (Object.keys(changes).length) await clubApi.update(id, changes);
      }
      try {
        if (typeof logo === 'string') await clubApi.setLogo(id, logo);
        else if (logo === null && club?.logoUrl) await clubApi.removeLogo(id);
      } catch (e) {
        // The club itself is saved; say only the logo failed.
        toast({ kind: 'error', title: t('Could not update logo'), body: e instanceof ApiError ? e.message : undefined });
      }
      invalidateClubs(qc);
      toast({ kind: 'success', title: t(club ? 'Club updated' : 'Club created') });
      onDone(id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('Something went wrong'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h3 className="font-display text-2xl font-bold uppercase">{t(club ? 'Edit club' : 'Create a club')}</h3>

      <div className="mt-4 flex items-center gap-4">
        <ClubBadge club={preview} size="lg" link={false} />
        <div className="flex flex-col items-start gap-2">
          <Button variant="secondary" size="sm" disabled={!online || busy} onClick={() => fileInput.current?.click()} data-testid="club-logo-pick">
            {t(shownLogo ? 'Change logo' : 'Add logo')}
          </Button>
          {shownLogo ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setLogo(club?.logoUrl ? null : undefined)} data-testid="club-logo-remove">
              {t('Remove logo')}
            </Button>
          ) : (
            <span className="text-xs text-ink-500">{t('Optional · square images work best')}</span>
          )}
        </div>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="club-logo-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void pick(file);
          }}
        />
      </div>

      <label className={labelCls} htmlFor="club-name">
        {t('Name')}
      </label>
      <input id="club-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} className={`${field} h-12 px-4`} data-testid="club-name-input" />
      <label className={labelCls} htmlFor="club-desc">
        {t('Description (optional)')}
      </label>
      <textarea id="club-desc" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} rows={3} className={`${field} p-3`} data-testid="club-desc-input" />
      {error ? <p className="mt-2 text-sm text-enemy">{error}</p> : null}
      <Button className="mt-4 w-full" size="lg" onClick={submit} loading={busy} disabled={!online} data-testid="create-club-submit">
        {t(club ? 'Save' : 'Create')}
      </Button>
    </>
  );
}
